import Vue from "vue";
import WebsocketChat from "../../websocket/websocket_chat";
import EventBus from "../../event/event-bus";

export default {
  state: {
    conn: null,
    connected: false,
    currentUserId: null,
    conversations: [],
    conversationMessages: {},
    emailToUuid: {}
  },
  mutations: {
    SET_CONNECTION(state, conn) {
      state.conn = conn;
    },
    SET_CONNECTED(state) {
      state.connected = true;
    },
    SET_CURRENT_USER_ID(state, id) {
      state.currentUserId = id;
    },
    SET_CONVERSATIONS(state, conversation) {
      state.conversations = conversation;
    },
    ADD_CONVERSATION(state, conversation) {
      state.conversations.push(conversation);
    },
    UPDATE_CONVERSATION(state, {index, conversation}) {
      Vue.set(state.conversations, index, conversation);
    },
    SET_CONVERSATION_MESSAGES(state, {uuid, messages}) {
      Vue.set(state.conversationMessages, uuid, messages);
    },
    ADD_CONVERSATION_MESSAGE(state, {uuid, message}) {
      if (state.conversationMessages[uuid] === undefined) {
        Vue.set(state.conversationMessages, uuid, []);
      }
      state.conversationMessages[uuid].push(message);
    },
    ADD_EMAIL_TO_UUID(state, {email, uuid}) {
      Vue.set(state.emailToUuid, email, uuid);
    }
  },
  actions: {
    startListenChat(context) {
      if (context.getters.chatWebsocketInstance) {
        return;
      }
      //Get current user ID
      context.dispatch('fetchChatCurrentUserId', () => {
        //Get current user friends
        context.dispatch('fetchCurrentUserConversations');
      });
      //Connect to chat websocket
      const websocketChat = new WebsocketChat();
      try {
        websocketChat.connect();
        //Register chat event listeners
        websocketChat.socket.on('update_chat', (data) => {
          const currentUserId = context.getters.chatCurrentUserId;
          const ownMessage = data.author == currentUserId;
          if (!ownMessage && currentUserId != data.to_user_id) {
            //This message not for current user
            return;
          }
          const uuid = ownMessage ? data.to_user_id : data.author;
          const conversationIndex = context.getters.conversationIndex(uuid);
          if (conversationIndex < 0) {
            //Add new conversation
            context.commit('ADD_CONVERSATION', {uuid: uuid, count: 0});
            //Retrieve new conversation user name
            context.dispatch('fetchConversationUserName', uuid);
          }
          //Add new message or load all conversation history
          if (context.getters.conversationMessagesLoaded(uuid)) {
            context.commit('ADD_CONVERSATION_MESSAGE', {
              uuid: uuid,
              message: data
            });
            //Increment conversation messages count
            context.commit('UPDATE_CONVERSATION', {
              index: conversationIndex,
              conversation: Object.assign({}, context.state.conversations[conversationIndex], {
                count: ++context.state.conversations[conversationIndex].count
              })
            });
          } else {
            context.dispatch('fetchConversationMessages', uuid);
          }
          //Broadcast event about new message to current user
          EventBus.$emit('chat:new_message', {uuid: uuid, ownMessage: ownMessage, message: data});
          //Play audio
          if (!ownMessage) {
            const audio = new Audio(process.env.VUE_APP_API_URL + '/users/audio/messege.mp3');
            audio.play().then(() => {});
          }
        });
        //Listen update conversations state
        websocketChat.socket.on('update_online', (data) => {
          for (let i in data) {
            const index = context.getters.conversationIndex(data[i].usrId);
            if (index >= 0) {
              context.commit('UPDATE_CONVERSATION', {
                index: index,
                conversation: Object.assign({}, context.state.conversations[index], {
                  status: data[i].status
                })
              });
            }
          }
          context.dispatch('sendChatHeartbeat');
        });
        //Listen websocket errors
        websocketChat.socket.on('error', (e) => {
          console.log('Chat websocket error', e);
        });
        //Listen connect event
        websocketChat.socket.on('connect', () => {
          context.commit('SET_CONNECTED');
          context.dispatch('sendChatHeartbeat');
        });
        //Store websocket chat instance
        context.commit('SET_CONNECTION', websocketChat);
      } catch (e) {
        console.log('Chat connection error', e);
      }
    },

    fetchChatCurrentUserId(context, callback) {
      Vue.axios.post('/users/ajax/get_id', {}, {
        headers: {'X-Requested-With': 'XMLHttpRequest'}
      }).then(({data}) => {
        if (data.success) {
          context.commit('SET_CURRENT_USER_ID', data.result.id);
          if (callback) {
            callback();
          }
        }
      });
    },

    fetchCurrentUserConversations(context) {
      Vue.axios.post('/users/ajax/get_friends', {'user_id': context.getters.chatCurrentUserId}, {
        headers: {'X-Requested-With': 'XMLHttpRequest'}
      }).then(({data}) => {
        if (data.success) {
          context.commit('SET_CONVERSATIONS', data.result);
        }
      });
    },

    fetchConversationUserName(context, uuid) {
      const conversationIndex = context.getters.conversationIndex(uuid);
      if (conversationIndex >= 0) {
        Vue.axios.get('/users/ajax/get_username/' + uuid, {
          headers: {'X-Requested-With': 'XMLHttpRequest'}
        }).then(({data}) => {
          if (data.success) {
            context.commit('UPDATE_CONVERSATION', {
              index: conversationIndex,
              conversation: Object.assign({}, context.state.conversations[conversationIndex], {
                name: data.result.firstname+" "+data.result.lastname
              })
            });
          }
        });
      }
    },

    fetchConversationMessages(context, uuid) {
      Vue.axios.get('/users/get_message/' + uuid, {
        headers: {'X-Requested-With': 'XMLHttpRequest'}
      }).then(({data}) => {
        if (data.result === 'success') {
          context.commit('SET_CONVERSATION_MESSAGES', {
            uuid: uuid,
            messages: data.messages
          });
          const index = context.getters.conversationIndex(uuid);
          context.commit('UPDATE_CONVERSATION', {
            index: index,
            conversation: Object.assign({}, context.state.conversations[index], {
              count: data.messages.length,
              loaded: true
            })
          });
        }
      });
    },

    fetchUserUuidFromEmail(context, email) {
      return new Promise((resolve, reject) => {
        if (context.state.emailToUuid[email] !== undefined) {
          resolve(context.state.emailToUuid[email]);
        } else {
          Vue.axios.post('/user/get_uuid_from_email', {'email': email}).then(({data}) => {
            if (data.result) {
              context.commit('ADD_EMAIL_TO_UUID', {email: email, uuid: data.message});
              resolve(data.message);
            } else {
              reject(data.message);
            }
          }).catch((e) => {
            reject(e);
          })
        }
      });
    },

    sendChatHeartbeat(context) {
      setTimeout(() => {
        context.getters.chatWebsocketInstance.socket.emit('online', context.getters.conversationUuidList);
      }, 10000);
    },

    sendChatMessage(context, {conversationUuid, message}) {
      context.getters.chatWebsocketInstance.socket.emit('message', {
        'userid': conversationUuid,
        'message': message
      });
    },

    addConversation(context, uuid) {
      return new Promise((resolve, reject) => {
        const index = context.getters.conversationIndex(uuid);
        if (index >= 0) {
          resolve();
          return;
        }
        Vue.axios.post('/users/ajax/add_friend', {'userId': uuid}, {
          headers: {'X-Requested-With': 'XMLHttpRequest'}
        }).then(({data}) => {
          context.commit('ADD_CONVERSATION', {uuid: uuid, name: data.result.name, count: 0});
          resolve(data.result);
        }).catch((e) => {
          reject(e);
        })
      });
    }
  },

  getters: {
    chatWebsocketInstance: state => {
      return state.conn;
    },
    chatConnected: state => {
      return state.connected;
    },
    chatCurrentUserId: state => {
      return state.currentUserId;
    },
    conversationList: state => {
      return state.conversations;
    },
    conversationIndex: state => uuid => {
      return state.conversations.findIndex(conversation => conversation.uuid == uuid);
    },
    conversationByUuid: state => uuid => {
      for (let i in state.conversations) {
        if (state.conversations[i].uuid == uuid) {
          return state.conversations[i];
        }
      }

      return undefined;
    },
    conversationMessagesLoaded: state => uuid => {
      const index = state.conversations.findIndex(conversation => conversation.uuid == uuid);
      return index >= 0 && !!state.conversations[index].loaded;
    },
    conversationUuidList: state => {
      return state.conversations.map(conversation => conversation.uuid);
    },
    conversationMessageList: state => uuid => {
      if (state.conversationMessages[uuid] !== undefined) {
        return state.conversationMessages[uuid];
      }

      return [];
    }
  }
}
